import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { Project } from 'dashboard/types';
import { BatchCreationType, FluencyLevel, UseCaseType, ProjectType } from '@scale/shared/defaults';
import { TaskType, TaskPipelineName } from '@scale/shared-types';
import {
  IdempotencyKey,
  RapidPlugAndPlayType,
  RapidProjectUseCase,
} from '@scale/shared/selfServe/defaults';
import API from '@scale/frontend-lib/dashboard-api';
import {
  getDefaultPipelineForProject,
  useRapidHistory,
} from 'dashboard/components/SelfServe/helpers';
import { Languages } from '@scale/shared/types/demographics';
import {
  SensitiveDataSelectionState,
  SensitiveDataType,
} from 'react-scale/dashboard.scale.com/components/SelfServe/Onboarding/Data/components/constants';
import _ from 'lodash';
import {
  useProjectAttachmentHipaaCoverage,
  useOnboardingV3Tour,
  TourStepElem,
  TourStep,
} from 'dashboard/components/SelfServe/Data/upload/hooks';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { isStudioProject, isRapidProject } from 'react-scale/studio/helpers';

export enum OnboardingView {
  Creation = 'creation',
  DataUpload = 'dataupload',
  DataView = 'dataview',
  ProjectConfig = 'projectconfig',
  Taxonomy = 'taxonomy',
  Instruction = 'instruction',
  Task = 'task',
  Workforce = 'workforce',
  DataSelect = 'dataselect',
  ProjectSettings = 'projectsettings',
  Payment = 'payment',
  Complete = 'complete',
}

export enum OnboardingStepNames {
  Creation = 'creation',
  Data = 'data',
  ProjectConfig = 'projectconfig',
  Taxonomy = 'taxonomy',
  Instruction = 'instruction',
  Task = 'task',
  Batch = 'batch',
  Summary = 'summary',
}

export enum OnboardingStepStatus {
  Incomplete = 'incomplete',
  Complete = 'complete',
  Locked = 'locked',
}

export enum EditTaxonomySteps {
  View = 'view',
  Taxonomy = 'editTaxonomy',
  Changes = 'propagateChanges',
  Instructions = 'editInstructions',
}

type OnboardingContextType = {
  isOnboardingComplete: boolean | undefined;
  setIsOnboardingComplete: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  loading: boolean | undefined;
  setLoading: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  project: Project | undefined | null;
  setProject: React.Dispatch<React.SetStateAction<Project | undefined | null>>;
  currentView: OnboardingView | undefined;
  setCurrentView: React.Dispatch<React.SetStateAction<OnboardingView | undefined>>;
  currentStep: OnboardingStepNames | undefined;
  setCurrentStep: React.Dispatch<React.SetStateAction<OnboardingStepNames | undefined>>;
  hasAssets: boolean | undefined;
  setHasAssets: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  onboardingSteps:
    | {
        name: OnboardingStepNames;
        status: OnboardingStepStatus;
      }[];
  setOnboardingSteps: React.Dispatch<
    React.SetStateAction<
      | {
          name: OnboardingStepNames;
          status: OnboardingStepStatus;
        }[]
    >
  >;
  firstBatchSelectedMap: any | undefined;
  setFirstBatchSelectedMap: React.Dispatch<React.SetStateAction<any | undefined>>;
  selectionQueryFilter: string | undefined;
  setSelectionQueryFilter: React.Dispatch<React.SetStateAction<string | undefined>>;
  firstBatchSelectionMethod: any | undefined;
  setFirstBatchSelectionMethod: React.Dispatch<React.SetStateAction<any | undefined>>;
  firstBatchNumTasks: number | undefined;
  setFirstBatchNumTasks: React.Dispatch<React.SetStateAction<number | undefined>>;
  numAssets: number | undefined;
  setNumAssets: React.Dispatch<React.SetStateAction<number | undefined>>;
  taskCount: number | undefined;
  setTaskCount: React.Dispatch<React.SetStateAction<number | undefined>>;
  costEstimate: string | undefined;
  setCostEstimate: React.Dispatch<React.SetStateAction<string | undefined>>;
  taskType: TaskType | undefined;
  setTaskType: React.Dispatch<React.SetStateAction<TaskType | undefined>>;
  projectUseCase: RapidProjectUseCase | undefined;
  setProjectUseCase: React.Dispatch<React.SetStateAction<RapidProjectUseCase | undefined>>;
  finalizeOnboarding: () => void;
  nextEnabled: boolean | undefined;
  setNextEnabled: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  batchSelected: BatchCreationType | undefined;
  setBatchSelected: React.Dispatch<React.SetStateAction<BatchCreationType | undefined>>;
  batchName: string | undefined;
  setBatchName: React.Dispatch<React.SetStateAction<string | undefined>>;
  isSelfLabel: boolean | undefined;
  projectOptions: any | undefined;
  setProjectOptions: React.Dispatch<React.SetStateAction<any | undefined>>;
  useOldOnboarding: boolean | undefined;
  setUseOldOnboarding: React.Dispatch<React.SetStateAction<boolean>>;
  sensitiveDataSelections: SensitiveDataSelectionState;
  setSensitiveDataSelections: React.Dispatch<React.SetStateAction<SensitiveDataSelectionState>>;
  sensitiveDataQuestionnaireCompleted: boolean | undefined;
  setSensitiveDataQuestionnaireCompleted: React.Dispatch<React.SetStateAction<boolean>>;
  useCaseType: UseCaseType | undefined; // deprecated
  setUseCaseType: React.Dispatch<React.SetStateAction<UseCaseType | undefined>>; // deprecated
  footerShown: boolean | undefined;
  setFooterShown: React.Dispatch<React.SetStateAction<boolean>>;
  stepNext: any | undefined;
  setStepNext: React.Dispatch<React.SetStateAction<any | undefined>>;
  disabledNextText: string | undefined;
  setDisabledNextText: React.Dispatch<React.SetStateAction<string | undefined>>;
  isAttachmentHipaaCovered: boolean | undefined;
  hipaaCoverageRefresh: any;
  populatePriceEstimator: boolean | undefined;
  setPopulatePriceEstimator: React.Dispatch<React.SetStateAction<boolean>>;
  firstTimeTemplate: boolean | undefined;
  setFirstTimeTemplate: React.Dispatch<React.SetStateAction<boolean>>;
  useOnboardingV3: boolean;
  canUsePlugAndPlay: boolean;
  usePlugAndPlayOnboarding: boolean | undefined;
  plugAndPlayV2: boolean;
  collapseSidebar: boolean | undefined;
  setCollapseSidebar: React.Dispatch<React.SetStateAction<boolean>>;
  templatePipeline: TaskPipelineName | undefined;
  setTemplatePipeline: React.Dispatch<React.SetStateAction<TaskPipelineName | undefined>>;
  handleExitTour: () => void;
  handleEnterTour: () => void;
  resetTour: () => void;
  popperIsOpen: boolean;
  tourRef: $FIXME;
  setTourRef: $FIXME;
  currentTourStep: TourStepElem | null;
  handleNextTourStep: () => void;
  syncTourStep: (step: TourStep) => void;
  closedTourSteps: TourStep[];
  closeTourStep: (step: TourStep) => void;
  closeUpToCurrentView: (step: TourStep) => void;
  onboardingStudioProject: boolean;
  refreshConfigs: () => void;
  showStudioLaunchPage: boolean;
  setShowStudioLaunchPage: React.Dispatch<React.SetStateAction<boolean>>;
  revertNewAuditUI: boolean;
  setRevertNewAuditUI: React.Dispatch<React.SetStateAction<boolean>>;
  launchError: string;
};

const OnboardingContext = React.createContext<OnboardingContextType>({
  isOnboardingComplete: undefined,
  setIsOnboardingComplete: () => {},
  loading: undefined,
  setLoading: () => {},
  project: undefined,
  setProject: () => {},
  currentView: undefined,
  setCurrentView: () => {},
  currentStep: undefined,
  setCurrentStep: () => {},
  hasAssets: undefined,
  setHasAssets: () => {},
  onboardingSteps: [],
  setOnboardingSteps: () => {},
  firstBatchSelectedMap: undefined,
  setFirstBatchSelectedMap: () => {},
  selectionQueryFilter: undefined,
  setSelectionQueryFilter: () => {},
  firstBatchSelectionMethod: undefined,
  setFirstBatchSelectionMethod: () => {},
  firstBatchNumTasks: undefined,
  setFirstBatchNumTasks: () => {},
  numAssets: undefined,
  setNumAssets: () => {},
  taskCount: undefined,
  setTaskCount: () => {},
  costEstimate: undefined,
  setCostEstimate: () => {},
  taskType: undefined,
  setTaskType: () => {},
  projectUseCase: undefined,
  setProjectUseCase: () => {},
  finalizeOnboarding: () => {},
  nextEnabled: undefined,
  setNextEnabled: () => {},
  batchSelected: undefined,
  setBatchSelected: () => {},
  batchName: 'first_batch',
  setBatchName: () => {},
  isSelfLabel: false,
  projectOptions: undefined,
  setProjectOptions: () => {},
  useOldOnboarding: false,
  setUseOldOnboarding: () => {},
  sensitiveDataSelections: _.zipObject(
    Object.values(SensitiveDataType),
    new Array(Object.keys(SensitiveDataType).length).fill(false),
  ) as SensitiveDataSelectionState,
  setSensitiveDataSelections: () => {},
  sensitiveDataQuestionnaireCompleted: false,
  setSensitiveDataQuestionnaireCompleted: () => {},
  useCaseType: undefined,
  setUseCaseType: () => {},
  templatePipeline: undefined,
  setTemplatePipeline: () => {},
  footerShown: false,
  setFooterShown: () => {},
  stepNext: undefined,
  setStepNext: () => {},
  disabledNextText: undefined,
  setDisabledNextText: () => {},
  isAttachmentHipaaCovered: false,
  hipaaCoverageRefresh: () => {},
  populatePriceEstimator: false,
  setPopulatePriceEstimator: () => {},
  firstTimeTemplate: false,
  setFirstTimeTemplate: () => {},
  useOnboardingV3: false,
  canUsePlugAndPlay: false,
  usePlugAndPlayOnboarding: false,
  plugAndPlayV2: false,
  collapseSidebar: false,
  setCollapseSidebar: () => {},
  handleExitTour: () => {},
  handleEnterTour: () => {},
  resetTour: () => {},
  popperIsOpen: false,
  tourRef: null,
  setTourRef: () => {},
  currentTourStep: null,
  handleNextTourStep: () => {},
  syncTourStep: () => {},
  closeTourStep: () => {},
  closeUpToCurrentView: () => {},
  closedTourSteps: [],
  onboardingStudioProject: false,
  refreshConfigs: () => {},
  showStudioLaunchPage: false,
  setShowStudioLaunchPage: () => {},
  revertNewAuditUI: false,
  setRevertNewAuditUI: () => {},
  launchError: '',
});

interface OnboardingProps {
  project?: Project | null;
  taskType?: TaskType;
  children?: React.ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProps> = ({ children, ...props }) => {
  const [isOnboardingComplete, setIsOnboardingComplete] = useState<boolean>();
  const [loading, setLoading] = useState<boolean>();
  const [project, setProject] = useState<Project | null | undefined>(props.project); // eventually move states to useReducer
  const [currentView, setCurrentView] = useState<OnboardingView>();
  const [currentStep, setCurrentStep] = useState<OnboardingStepNames>();
  const [hasAssets, setHasAssets] = useState<boolean>();
  const [taskType, setTaskType] = useState<TaskType | undefined>(props.taskType);
  const [projectUseCase, setProjectUseCase] = useState<RapidProjectUseCase>();
  const [templatePipeline, setTemplatePipeline] = useState<TaskPipelineName | undefined>(undefined);
  const [nextEnabled, setNextEnabled] = useState<boolean>();
  const [onboardingSteps, setOnboardingSteps] = useState<any>([
    { name: OnboardingStepNames.Creation, status: OnboardingStepStatus.Complete },
    { name: OnboardingStepNames.Data, status: OnboardingStepStatus.Incomplete },
    { name: OnboardingStepNames.Task, status: OnboardingStepStatus.Locked },
    { name: OnboardingStepNames.Batch, status: OnboardingStepStatus.Locked },
    { name: OnboardingStepNames.Summary, status: OnboardingStepStatus.Locked },
  ]);
  const [firstBatchSelectedMap, setFirstBatchSelectedMap] = useState<any>();
  const [selectionQueryFilter, setSelectionQueryFilter] = useState<string>();
  const [firstBatchSelectionMethod, setFirstBatchSelectionMethod] = useState<any>();
  const [firstBatchNumTasks, setFirstBatchNumTasks] = useState<number>();
  const [numAssets, setNumAssets] = useState<number>();
  const [taskCount, setTaskCount] = useState<number>();
  const [costEstimate, setCostEstimate] = useState<string>();
  const [batchSelected, setBatchSelected] = useState<BatchCreationType>();
  const [batchName, setBatchName] = useState<string>();
  const isSelfLabel = useMemo(() => batchSelected === BatchCreationType.SelfLabel, [batchSelected]);
  const [projectOptions, setProjectOptions] = useState<Record<any, any>>();
  const [useOldOnboarding, setUseOldOnboarding] = useState<boolean>(false);
  const [sensitiveDataSelections, setSensitiveDataSelections] =
    useState<SensitiveDataSelectionState>(
      _.zipObject(
        Object.values(SensitiveDataType),
        new Array(Object.keys(SensitiveDataType).length).fill(false),
      ) as SensitiveDataSelectionState,
    );
  const [sensitiveDataQuestionnaireCompleted, setSensitiveDataQuestionnaireCompleted] =
    useState(false);
  const [useCaseType, setUseCaseType] = useState<UseCaseType>();
  const [footerShown, setFooterShown] = useState<boolean>(false);
  const [stepNext, setStepNext] = useState<any>();
  const [disabledNextText, setDisabledNextText] = useState<string>();
  const { isProjectAttachmentCoveredByHipaa, refresh: hipaaCoverageRefresh } =
    useProjectAttachmentHipaaCoverage(project?.id);
  const [populatePriceEstimator, setPopulatePriceEstimator] = useState<boolean>(false);
  const [firstTimeTemplate, setFirstTimeTemplate] = useState(false);
  const useOnboardingV3 = useUnleashFeatureFlag('rapid-onboarding-v3', {}, false) || false;
  const { isStudioUrl } = useRapidHistory();
  const canUsePlugAndPlay =
    (useUnleashFeatureFlag('rapid-plug-and-play', {}, false) && !isStudioUrl()) || false;
  const plugAndPlayV2 =
    (useUnleashFeatureFlag('rapid-plug-and-play-v2', {}, false) && !isStudioUrl()) || false;
  const usePlugAndPlayOnboarding = useMemo(() => {
    return (
      project?.rapidPlugAndPlayType &&
      [RapidPlugAndPlayType.Summarization, RapidPlugAndPlayType.Generation].includes(
        project.rapidPlugAndPlayType,
      )
    );
  }, [project]);
  const [collapseSidebar, setCollapseSidebar] = useState<boolean>(false);
  const [onboardingStudioProject, setOnboardingStudioProject] = useState<boolean>(false);
  const [showStudioLaunchPage, setShowStudioLaunchPage] = useState<boolean>(false);
  const [launchError, setLaunchError] = useState<string>('');
  const [fetchConfigDependency, setFetchConfigDependency] = useState<number>(0);
  const [revertNewAuditUI, setRevertNewAuditUI] = useState<boolean>(false);

  const {
    tourRef,
    setTourRef,
    currentTourStep,
    handleNextTourStep,
    syncTourStep,
    closeTourStep,
    closeUpToCurrentView,
    closedTourSteps,
    handleExitTour,
    handleEnterTour,
    resetTour,
  } = useOnboardingV3Tour(
    firstTimeTemplate,
    !project || project?.projectType === ProjectType.SelfServe,
  );
  const popperIsOpen: boolean =
    !_.isNil(tourRef) &&
    currentTourStep &&
    currentTourStep.name &&
    !closedTourSteps.includes(currentTourStep.name);

  // Changes task type of project from DataCollection (placeholder task type) to user's selection
  // Calls initialSetup on project
  const finalizeOnboarding = useCallback(() => {
    const finalize = async () => {
      setLaunchError('');
      if (onboardingStudioProject && isProjectAttachmentCoveredByHipaa) {
        // if first time making a studio hipaa project, enable customer-level coverage
        await API.selfserve.updateCustomerAttachmentHipaaCoverage({
          isAttachmentCoveredByHipaa: isProjectAttachmentCoveredByHipaa,
        });
      }
      const studioOverrideProjectOptions = {
        enableOcr: taskType === TaskType.DocumentTranscription,
        isAudioTranscription: false,
        containsAdultContent: false,
      };
      let tries = 3;
      let response: { success: boolean; project: Project } | undefined;
      while (tries > 0) {
        try {
          response = await API.selfserve.updateInitialSetup({
            project: project?.id,
            taskType,
            ...projectOptions,
            ...(onboardingStudioProject ? studioOverrideProjectOptions : {}),
            useLabelerManagementPlatform: !!onboardingStudioProject,
            skipOverrideValidators: true,
          });
          break;
        } catch {
          tries--;
        }
      }
      if (!response || !response.success) {
        setLaunchError(
          'Project configurations did not get initialized properly. Batch will not be launched. Please try creating another project or contact scale_studio@scale.com for support.',
        );
      } else {
        setProject(response.project);

        const body: any = {
          project: response.project.id,
          filterByHasTask: false,
          selectionQueryFilter,
        };
        if (project?.rapidPlugAndPlayGeneratePrompts) {
          body.size = taskCount; // todo: fix size
        } else if (firstBatchSelectionMethod) {
          body.choice = firstBatchSelectionMethod;
          body.size = firstBatchNumTasks;
        } else if (Object.keys(firstBatchSelectedMap as {}).length > 0) {
          body.uploadedAssetIds = Object.keys(firstBatchSelectedMap as {});
          body.size = Object.keys(firstBatchSelectedMap as {}).length;
        }
        if (
          batchSelected === BatchCreationType.RegularBatch ||
          batchSelected === BatchCreationType.SelfLabel
        ) {
          body.batchName = batchName;
          body.launch = project?.rapidPlugAndPlayType
            ? batchSelected !== BatchCreationType.SelfLabel
            : onboardingStudioProject; // regular batch for studio, self label for rapid
          await API.selfserve.createRegularBatch(body, {
            idempotencyKey: IdempotencyKey.regularBatch,
          });
        } else {
          await API.selfserve.createInstructionBatch(body, {
            idempotencyKey: IdempotencyKey.calibrationBatch,
          });
        }
      }
      // TODO: Add error handler { success, errorMsg } <= api calls return this
      if (onboardingStudioProject) {
        setShowStudioLaunchPage(true);
      }
      setIsOnboardingComplete(true);
      setFirstBatchNumTasks(undefined);
      setFirstBatchSelectedMap(undefined);
      setCurrentView(OnboardingView.Creation);
      setCurrentStep(OnboardingStepNames.Creation);
      setNumAssets(undefined);
      setHasAssets(undefined);
      setSelectionQueryFilter(undefined);
      setTaskCount(undefined);
      setCostEstimate(undefined);
      setBatchName(undefined);
      setFirstTimeTemplate(false);
      handleExitTour();
    };
    finalize();
  }, [
    batchSelected,
    batchName,
    firstBatchSelectedMap,
    project,
    projectOptions,
    selectionQueryFilter,
    firstBatchSelectionMethod,
    firstBatchNumTasks,
    taskType,
    onboardingStudioProject,
    isProjectAttachmentCoveredByHipaa,
  ]);

  useEffect(() => {
    setNumAssets(undefined);
    setHasAssets(undefined);
    if (project?.id) {
      // eslint-disable-next-line complexity
      const getProjectState = async () => {
        setIsOnboardingComplete(undefined);
        setLoading(true);
        const studioProject = isStudioProject(project);
        setOnboardingStudioProject(studioProject);
        setSensitiveDataSelections(
          _.zipObject(
            Object.values(SensitiveDataType),
            new Array(Object.keys(SensitiveDataType).length).fill(false),
          ) as SensitiveDataSelectionState,
        );
        const {
          regularBatchExists,
          calibrationBatchExists,
          selfLabelBatchExists,
          uploadedAssetsExist,
          taskExists,
          useCase,
          instruction,
          isLlmControlCenterProject,
        } = await API.selfserve.fetchState({
          project: project.id,
        });
        const projectTaxonomyData = await API.selfserve.fetchProjectTaxonomy({
          project: project.id,
        });
        let instructionsRes;
        try {
          if (useOnboardingV3) {
            instructionsRes = await API.selfserve.fetchProjectInstructionsV2({
              projectId: project.id,
              project: project.id,
            });
          } else {
            instructionsRes = await API.selfserve.fetchProjectInstructions({
              project: project.id,
            });
          }
        } catch (_e) {
          instructionsRes = undefined;
        }
        let onboardingComplete = Boolean(
          regularBatchExists ||
            calibrationBatchExists ||
            selfLabelBatchExists ||
            taskExists ||
            isLlmControlCenterProject,
        );
        // Make backwards compatible for Studio projects that don't have tasks but have instructions
        if (studioProject) {
          onboardingComplete = Boolean(onboardingComplete || instruction?.instructionObjectId);
        }
        setIsOnboardingComplete(onboardingComplete);
        if (isRapidProject(project)) setCollapseSidebar(!onboardingComplete);
        setProjectUseCase(useCase);
        setHasAssets(uploadedAssetsExist);
        const maybeSummaryStep = studioProject
          ? []
          : [{ name: OnboardingStepNames.Summary, status: OnboardingStepStatus.Locked }];
        if (usePlugAndPlayOnboarding) {
          if (!project.rapidPlugAndPlayGeneratePrompts) {
            setOnboardingSteps([
              { name: OnboardingStepNames.Creation, status: OnboardingStepStatus.Complete },
              { name: OnboardingStepNames.Data, status: OnboardingStepStatus.Incomplete },
              { name: OnboardingStepNames.Taxonomy, status: OnboardingStepStatus.Locked },
              { name: OnboardingStepNames.Instruction, status: OnboardingStepStatus.Locked },
              { name: OnboardingStepNames.Batch, status: OnboardingStepStatus.Locked },
              ...maybeSummaryStep,
            ]);
            setCurrentView(OnboardingView.DataView);
            setCurrentStep(OnboardingStepNames.Data);
          } else {
            setOnboardingSteps([
              { name: OnboardingStepNames.Creation, status: OnboardingStepStatus.Complete },
              { name: OnboardingStepNames.Taxonomy, status: OnboardingStepStatus.Incomplete },
              { name: OnboardingStepNames.Instruction, status: OnboardingStepStatus.Locked },
              { name: OnboardingStepNames.Batch, status: OnboardingStepStatus.Locked },
              ...maybeSummaryStep,
            ]);
            setCurrentView(OnboardingView.Taxonomy);
            setCurrentStep(OnboardingStepNames.Taxonomy);
          }
        } else {
          if (!uploadedAssetsExist) {
            if (useOnboardingV3) {
              setOnboardingSteps([
                { name: OnboardingStepNames.Creation, status: OnboardingStepStatus.Complete },
                { name: OnboardingStepNames.Data, status: OnboardingStepStatus.Incomplete },
                { name: OnboardingStepNames.ProjectConfig, status: OnboardingStepStatus.Locked },
                { name: OnboardingStepNames.Taxonomy, status: OnboardingStepStatus.Locked },
                { name: OnboardingStepNames.Instruction, status: OnboardingStepStatus.Locked },
                { name: OnboardingStepNames.Batch, status: OnboardingStepStatus.Locked },
                ...maybeSummaryStep,
              ]);
            } else {
              setOnboardingSteps([
                { name: OnboardingStepNames.Creation, status: OnboardingStepStatus.Complete },
                { name: OnboardingStepNames.Data, status: OnboardingStepStatus.Incomplete },
                { name: OnboardingStepNames.Task, status: OnboardingStepStatus.Locked },
                { name: OnboardingStepNames.Batch, status: OnboardingStepStatus.Locked },
                ...maybeSummaryStep,
              ]);
            }
          } else if (useOnboardingV3) {
            setOnboardingSteps([
              { name: OnboardingStepNames.Creation, status: OnboardingStepStatus.Complete },
              { name: OnboardingStepNames.Data, status: OnboardingStepStatus.Complete },
              { name: OnboardingStepNames.ProjectConfig, status: OnboardingStepStatus.Incomplete },
              { name: OnboardingStepNames.Taxonomy, status: OnboardingStepStatus.Locked },
              { name: OnboardingStepNames.Instruction, status: OnboardingStepStatus.Locked },
              { name: OnboardingStepNames.Batch, status: OnboardingStepStatus.Locked },
              ...maybeSummaryStep,
            ]);
            setCurrentView(OnboardingView.ProjectConfig);
            setCurrentStep(OnboardingStepNames.ProjectConfig);
          } else {
            setOnboardingSteps([
              { name: OnboardingStepNames.Creation, status: OnboardingStepStatus.Complete },
              { name: OnboardingStepNames.Data, status: OnboardingStepStatus.Complete },
              { name: OnboardingStepNames.Task, status: OnboardingStepStatus.Incomplete },
              { name: OnboardingStepNames.Batch, status: OnboardingStepStatus.Locked },
              ...maybeSummaryStep,
            ]);
            setCurrentView(OnboardingView.Task);
            setCurrentStep(OnboardingStepNames.Task);
          }
          if (useOnboardingV3) {
            if (useCase || projectTaxonomyData.taxonomy) {
              setOnboardingSteps([
                { name: OnboardingStepNames.Creation, status: OnboardingStepStatus.Complete },
                { name: OnboardingStepNames.Data, status: OnboardingStepStatus.Complete },
                { name: OnboardingStepNames.ProjectConfig, status: OnboardingStepStatus.Complete },
                { name: OnboardingStepNames.Taxonomy, status: OnboardingStepStatus.Incomplete },
                { name: OnboardingStepNames.Instruction, status: OnboardingStepStatus.Locked },
                { name: OnboardingStepNames.Batch, status: OnboardingStepStatus.Locked },
                ...maybeSummaryStep,
              ]);
              if (firstTimeTemplate) {
                setCurrentView(OnboardingView.DataView);
                setCurrentStep(OnboardingStepNames.Data);
              } else {
                setCurrentView(OnboardingView.Taxonomy);
                setCurrentStep(OnboardingStepNames.Taxonomy);
              }
            }
            if (instructionsRes?.instruction) {
              setOnboardingSteps([
                { name: OnboardingStepNames.Creation, status: OnboardingStepStatus.Complete },
                { name: OnboardingStepNames.Data, status: OnboardingStepStatus.Complete },
                { name: OnboardingStepNames.ProjectConfig, status: OnboardingStepStatus.Complete },
                { name: OnboardingStepNames.Taxonomy, status: OnboardingStepStatus.Complete },
                { name: OnboardingStepNames.Instruction, status: OnboardingStepStatus.Incomplete },
                { name: OnboardingStepNames.Batch, status: OnboardingStepStatus.Locked },
                ...maybeSummaryStep,
              ]);
              setCurrentView(OnboardingView.Instruction);
              setCurrentStep(OnboardingStepNames.Instruction);
            }
          }
        }
      };
      setLoading(false);
      getProjectState();
    } else if (typeof project?.id !== 'undefined') {
      setIsOnboardingComplete(false);
      setLoading(false);
    }
  }, [project?.id, useOnboardingV3]);

  const refreshConfigs = useCallback(() => {
    setFetchConfigDependency(prev => prev + 1);
  }, [setFetchConfigDependency]);

  useEffect(() => {
    if (project) {
      const fetchProjectOptions = async () => {
        setLoading(true);
        const {
          pipelineName,
          numReviews,
          consensus,
          requiredLanguage,
          languageExpertise,
          customOcrUrl,
          ocrProvider,
          commonErrors,
          autoRedos,
        } = await API.selfserve.fetchConfigValues({
          project: project.id,
        });
        setProjectOptions({
          isAudioTranscription: !!project.isAudioTranscription,
          containsAdultContent: !!project.containsAdultContent,
          pipelineName: pipelineName || getDefaultPipelineForProject(project.type),
          taskerLanguageRequirement: {
            language: requiredLanguage || Languages.English,
            level: languageExpertise || FluencyLevel.Basic,
          },
          numReviews,
          numConsensus: consensus,
          ...(onboardingStudioProject
            ? {
                ocrConfig: {
                  customUrl: customOcrUrl,
                  serviceProvider: ocrProvider,
                },
                commonErrors,
                autoRedos,
              }
            : {}),
        });
        setLoading(false);
      };
      fetchProjectOptions();
    }
  }, [project, fetchConfigDependency]);

  useEffect(() => {
    setUseOldOnboarding(!!project?.useOldOnboarding);
  }, [project?.useOldOnboarding]);

  useEffect(() => {
    if (project) {
      const fetchHipaaCoverage = async () => {
        const { isAttachmentCoveredByHipaa } =
          await API.selfserve.fetchProjectAttachmentHipaaCoverage({
            project: project.id,
          });
        setSensitiveDataSelections(prevState => ({
          ...prevState,
          [SensitiveDataType.HIPAA_ATTACHMENTS]: isAttachmentCoveredByHipaa,
        }));
      };
      fetchHipaaCoverage();
    }
  }, [project]);

  return (
    <OnboardingContext.Provider
      value={{
        isOnboardingComplete,
        setIsOnboardingComplete,
        loading,
        setLoading,
        project,
        setProject,
        currentView,
        setCurrentView,
        currentStep,
        hasAssets,
        setHasAssets,
        setCurrentStep,
        onboardingSteps,
        setOnboardingSteps,
        firstBatchSelectedMap,
        setFirstBatchSelectedMap,
        selectionQueryFilter,
        setSelectionQueryFilter,
        firstBatchSelectionMethod,
        setFirstBatchSelectionMethod,
        firstBatchNumTasks,
        setFirstBatchNumTasks,
        numAssets,
        setNumAssets,
        taskCount,
        setTaskCount,
        costEstimate,
        setCostEstimate,
        taskType,
        setTaskType,
        projectUseCase,
        setProjectUseCase,
        finalizeOnboarding,
        nextEnabled,
        setNextEnabled,
        batchSelected,
        setBatchSelected,
        batchName,
        setBatchName,
        isSelfLabel,
        projectOptions,
        setProjectOptions,
        useOldOnboarding,
        setUseOldOnboarding,
        sensitiveDataSelections,
        setSensitiveDataSelections,
        sensitiveDataQuestionnaireCompleted,
        setSensitiveDataQuestionnaireCompleted,
        useCaseType,
        setUseCaseType,
        footerShown,
        setFooterShown,
        stepNext,
        setStepNext,
        disabledNextText,
        setDisabledNextText,
        isAttachmentHipaaCovered: isProjectAttachmentCoveredByHipaa,
        hipaaCoverageRefresh,
        populatePriceEstimator,
        setPopulatePriceEstimator,
        firstTimeTemplate,
        setFirstTimeTemplate,
        useOnboardingV3,
        canUsePlugAndPlay,
        usePlugAndPlayOnboarding,
        plugAndPlayV2,
        collapseSidebar,
        setCollapseSidebar,
        templatePipeline,
        setTemplatePipeline,
        handleExitTour,
        handleEnterTour,
        resetTour,
        popperIsOpen,
        tourRef,
        setTourRef,
        currentTourStep,
        handleNextTourStep,
        syncTourStep,
        closedTourSteps,
        closeTourStep,
        closeUpToCurrentView,
        onboardingStudioProject,
        refreshConfigs,
        showStudioLaunchPage,
        setShowStudioLaunchPage,
        revertNewAuditUI,
        setRevertNewAuditUI,
        launchError,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => useContext(OnboardingContext);
